'use client'

import Link from 'next/link'
import { Trans } from 'react-i18next'
import { Box, Grid, Typography } from '@mui/material'

// Types
import type { TimelineItemProps } from '@/types/components/molecules/timeline/item'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Fonts
import RobotoCondensedFont from '@/styles/fonts/roboto-condensed'

const TimelineItemMolecule = (props: TimelineItemProps) => {
	// Props
	const { lng, title, link, image, year, description, isFirst, isLast } = props

	// Variables
	const { t } = useTranslation(lng)

	return (
		<Grid size={12} sx={{ '& a': { color: 'inherit', textDecoration: 'none' } }}>
			<Box sx={{ display: 'flex', alignItems: 'stretch', width: 1 }}>
				{/* Rail + dot column */}
				<Box
					sx={{
						position: 'relative',
						flexShrink: 0,
						display: 'flex',
						justifyContent: 'center',
						width: { xs: 56, md: 96 }
					}}
				>
					{/* Vertical rail */}
					{!(isFirst && isLast) && (
						<Box
							sx={{
								position: 'absolute',
								left: '50%',
								top: isFirst ? { xs: 32, md: 52 } : { xs: -8, md: 0 },
								height: isLast
									? (isFirst ? { xs: 8, md: 4 } : { xs: 40, md: 56 })
									: isFirst
										? { xs: 'calc(100% - 24px)', md: 'calc(100% - 52px)' }
										: { xs: 'calc(100% + 16px)', md: '100%' },
								width: 3,
								bgcolor: 'primary.main',
								transform: 'translateX(-50%)'
							}}
						/>
					)}
					{/* Year dot */}
					<Box
						sx={{
							position: 'relative',
							zIndex: 1,
							alignSelf: 'flex-start',
							mt: { xs: 1, md: 2 },
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: { xs: 48, md: 72 },
							height: { xs: 48, md: 72 },
							borderRadius: '50%',
							bgcolor: 'primary.main'
						}}
					>
						<Typography
							component="span"
							fontFamily={RobotoCondensedFont.style.fontFamily}
							fontWeight={400}
							fontSize={{ xs: 16, md: 22 }}
							lineHeight={1}
							textAlign="center"
							color="#fff"
						>
							{year}
						</Typography>
					</Box>
				</Box>

				{/* Card */}
				<Box sx={{ flex: 1, py: { xs: 1, md: 2 }, pl: { xs: 1, md: 2 }, minWidth: 0 }}>
					<Link href={`/${lng}${link}`}>
						<Box
							sx={{
								bgcolor: 'background.paper',
								border: '1px solid',
								borderColor: 'rgba(0, 0, 0, 0.08)',
								borderRadius: 2,
								boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04)',
								p: { xs: 2, md: 3 },
								transition: 'box-shadow .2s ease, transform .2s ease',
								'&:hover': {
									boxShadow: '0 4px 16px rgba(0, 0, 0, 0.10)',
									transform: 'translateY(-1px)'
								}
							}}
						>
							<Grid container spacing={{ xs: 2, md: 3 }} alignItems="center" flexWrap={{ md: 'nowrap' }}>
								{image && (
									<Grid size={{ xs: 12, md: 'auto' }}>
										<Box
											sx={{
												width: { xs: 1, md: 260 },
												borderRadius: 2,
												overflow: 'hidden',
												'& img': { width: 1, height: 'auto', display: 'block' }
											}}
										>
											<picture>
												<img src={image} alt={title} />
											</picture>
										</Box>
									</Grid>
								)}

								<Grid size={{ xs: 12, md: 'grow' }} sx={{ minWidth: 0 }}>
									{title && (
										<Typography
											fontSize={{ xs: 20, md: 24 }}
											fontWeight={700}
											color="secondary.main"
											mb={1}
											sx={{ textDecoration: 'underline' }}
										>
											{t(title)}
										</Typography>
									)}

									{description && (
										<Typography fontSize={16} fontWeight={400} color="primary.text">
											<Trans>{description}</Trans>
										</Typography>
									)}
								</Grid>
							</Grid>
						</Box>
					</Link>
				</Box>
			</Box>
		</Grid>
	)
}

export default TimelineItemMolecule
